import React from 'react';
import { School, Users, GraduationCap, Heart, BookOpen, ShieldCheck, Activity, TrendingUp, AlertTriangle, CheckCircle2, Clock, Server, RefreshCw, Tag } from 'lucide-react';
import { motion } from 'framer-motion';
import { PageLayout } from '../../shared/PageLayout';
import { StatCard } from '../../shared/StatCard';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { QualityRing, ProgressBar, containerVariants, itemVariants } from './dashboard/DashboardCharts';
import { useDashboardData } from './dashboard/useDashboardData';
export function DashboardPage({
  onNavigate
}: {
  onNavigate?: (view: string) => void;
}) {
  const {
    stats,
    contentStats,
    pipelineLoading,
    strictModeOn,
    handleRefreshPipeline,
    passCount,
    warnCount,
    failCount,
    avgScore,
    totalValidated,
    systemHealth,
    recentActivity
  } = useDashboardData();
  return <PageLayout title="Dashboard" subtitle="Overzicht van het Meester djay platform" actions={<ConnectionBadge status="live" data-id="element-3993" />} data-id="element-3992">
      {/* Section 1: Platform Overview */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6" data-id="element-3994">
        <motion.div variants={itemVariants} data-id="element-3995">
          <StatCard label="Leerlingen" value={stats.totalLearners} icon={<Users className="w-4 h-4 text-blue-500" data-id="element-3997" />} data-id="element-3996" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-3998">
          <StatCard label="Docenten" value={stats.totalTeachers} icon={<GraduationCap className="w-4 h-4 text-indigo-500" data-id="element-4000" />} data-id="element-3999" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-4001">
          <StatCard label="Ouders" value={stats.totalParents} icon={<Heart className="w-4 h-4 text-rose-500" data-id="element-4003" />} data-id="element-4002" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-4004">
          <StatCard label="Scholen" value={stats.totalSchools} icon={<School className="w-4 h-4 text-purple-500" data-id="element-4006" />} data-id="element-4005" />
        </motion.div>
      </motion.div>

      {/* Section 2: Content & Quality */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5 mb-6" data-id="element-4007">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-4008">
          Content & Kwaliteit
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-4009">
          {/* Left: Content Stats */}
          <div className="space-y-4" data-id="element-4010">
            <div className="flex items-center justify-between" data-id="element-4011">
              <div className="flex items-center gap-2" data-id="element-4012">
                <BookOpen className="w-4 h-4 text-blue-500" data-id="element-4013" />
                <span className="text-sm font-medium text-gray-700" data-id="element-4014">
                  Vragenbank
                </span>
              </div>
              <span className="text-lg font-bold text-gray-900" data-id="element-4015">
                {contentStats.totalQuestions.toLocaleString('nl')}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-4016">
              <div className="flex items-center gap-2" data-id="element-4017">
                <Tag className="w-4 h-4 text-purple-500" data-id="element-4018" />
                <span className="text-sm font-medium text-gray-700" data-id="element-4019">
                  Families
                </span>
              </div>
              <span className="text-sm text-gray-600" data-id="element-4020">
                {contentStats.totalFamilies} / {contentStats.taxonomyFamilies}
              </span>
            </div>
            <ProgressBar value={contentStats.coveragePct} color={contentStats.coveragePct >= 80 ? 'bg-emerald-500' : contentStats.coveragePct >= 50 ? 'bg-amber-500' : 'bg-red-500'} label="Seed dekking" data-id="element-4021" />
            <ProgressBar value={contentStats.visualCoveragePct} color={contentStats.visualCoveragePct >= 80 ? 'bg-emerald-500' : contentStats.visualCoveragePct >= 50 ? 'bg-amber-500' : 'bg-red-500'} label={`Visuele componenten (${contentStats.usedVisualsCount}/${contentStats.registeredVisuals})`} data-id="element-4022" />
          </div>

          {/* Right: Quality & Validation */}
          <div className="space-y-4" data-id="element-4023">
            <div className="flex items-center justify-between" data-id="element-4024">
              <div className="flex items-center gap-3" data-id="element-4025">
                {avgScore !== null ? <QualityRing score={avgScore} data-id="element-4026" /> : <div className="w-12 h-12 rounded-full bg-gray-100 animate-pulse" data-id="element-4027" />}
                <div data-id="element-4028">
                  <div className="text-sm font-medium text-gray-700" data-id="element-4029">
                    Kwaliteitsscore
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-4030">
                    alle {contentStats.totalQuestions.toLocaleString('nl')}{' '}
                    vragen
                  </div>
                </div>
              </div>
              <button onClick={handleRefreshPipeline} disabled={pipelineLoading} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40" title="Validatie vernieuwen" data-id="element-4031">
                <RefreshCw className={`w-4 h-4 ${pipelineLoading ? 'animate-spin' : ''}`} data-id="element-4032" />
              </button>
            </div>

            {pipelineLoading ? <div className="flex gap-4" data-id="element-4033">
                {['Geslaagd', 'Warnings', 'Fouten'].map(l => <div key={l} className="flex-1 bg-gray-50 rounded-lg p-2 animate-pulse" data-id="element-4034">
                    <div className="h-5 bg-gray-200 rounded mb-1" data-id="element-4035" />
                    <div className="h-3 bg-gray-100 rounded w-2/3" data-id="element-4036" />
                  </div>)}
              </div> : passCount !== null ? <>
                <div className="flex gap-3" data-id="element-4037">
                  <div className="flex-1 bg-emerald-50 rounded-lg p-2.5 text-center" data-id="element-4038">
                    <div className="text-lg font-bold text-emerald-700" data-id="element-4039">
                      {passCount}
                    </div>
                    <div className="text-xs text-emerald-600" data-id="element-4040">Geslaagd</div>
                  </div>
                  <div className="flex-1 bg-amber-50 rounded-lg p-2.5 text-center" data-id="element-4041">
                    <div className="text-lg font-bold text-amber-700" data-id="element-4042">
                      {warnCount}
                    </div>
                    <div className="text-xs text-amber-600" data-id="element-4043">Warnings</div>
                  </div>
                  <div className="flex-1 bg-red-50 rounded-lg p-2.5 text-center" data-id="element-4044">
                    <div className="text-lg font-bold text-red-700" data-id="element-4045">
                      {failCount}
                    </div>
                    <div className="text-xs text-red-600" data-id="element-4046">Fouten</div>
                  </div>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex" data-id="element-4047">
                  <div className="h-full bg-emerald-500" style={{
                width: `${totalValidated > 0 ? (passCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-4048" />
                  <div className="h-full bg-amber-400" style={{
                width: `${totalValidated > 0 ? (warnCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-4049" />
                  <div className="h-full bg-red-500" style={{
                width: `${totalValidated > 0 ? (failCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-4050" />
                </div>
              </> : <div className="text-sm text-gray-400 text-center py-4" data-id="element-4051">
                Validatie wordt geladen...
              </div>}
          </div>
        </div>
      </motion.div>

      {/* Section 3: Activity & System Health */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6" data-id="element-4052">
        {/* Recent Activity */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4053">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4054">
            <Clock className="w-4 h-4" data-id="element-4055" /> Recente Activiteit
          </h3>
          <div className="space-y-3" data-id="element-4056">
            {recentActivity.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-4057">
                <div className={`mt-0.5 flex-shrink-0 ${item.type === 'success' ? 'text-emerald-500' : item.type === 'warning' ? 'text-amber-500' : 'text-blue-500'}`} data-id="element-4058">
                  {item.type === 'success' ? <CheckCircle2 className="w-4 h-4" data-id="element-4059" /> : item.type === 'warning' ? <AlertTriangle className="w-4 h-4" data-id="element-4060" /> : <Activity className="w-4 h-4" data-id="element-4061" />}
                </div>
                <div className="min-w-0 flex-1" data-id="element-4062">
                  <p className="text-sm text-gray-700 leading-snug" data-id="element-4063">
                    {item.message}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5 font-mono" data-id="element-4064">
                    {item.time}
                  </p>
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* System Health */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4065">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4066">
            <Server className="w-4 h-4" data-id="element-4067" /> Systeemgezondheid
          </h3>
          <div className="space-y-4" data-id="element-4068">
            <div className="flex items-center justify-between" data-id="element-4069">
              <span className="text-sm text-gray-600" data-id="element-4070">Strict Mode</span>
              <span className={`text-xs font-bold px-2 py-1 rounded-full ${strictModeOn ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-4071">
                {strictModeOn ? 'AAN' : 'UIT'}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-4072">
              <span className="text-sm text-gray-600" data-id="element-4073">Gem. responstijd</span>
              <span className="text-sm font-semibold text-gray-800" data-id="element-4074">
                {systemHealth.responseTime}ms
              </span>
            </div>
            <div data-id="element-4075">
              <div className="flex items-center justify-between mb-1.5" data-id="element-4076">
                <span className="text-sm text-gray-600" data-id="element-4077">Opslag</span>
                <span className="text-sm font-semibold text-gray-800" data-id="element-4078">
                  {systemHealth.storageMb} / 5 MB
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-4079">
                <div className="h-full bg-violet-500 rounded-full" style={{
                width: `${systemHealth.storagePct}%`
              }} data-id="element-4080" />
              </div>
            </div>
            <div data-id="element-4081">
              <div className="flex items-center justify-between mb-1.5" data-id="element-4082">
                <span className="text-sm text-gray-600" data-id="element-4083">Actief deze week</span>
                <span className="text-sm font-semibold text-gray-800" data-id="element-4084">
                  {stats.activeThisWeek} leerlingen
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-4085">
                <div className="h-full bg-blue-500 rounded-full" style={{
                width: `${Math.min(100, stats.activeThisWeek / Math.max(stats.totalLearners, 1) * 100)}%`
              }} data-id="element-4086" />
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Section 4: Quick Insights */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4" data-id="element-4087">
        {/* Subscriptions */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4088">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-4089">
            Abonnementen
          </h3>
          <div className="space-y-2.5" data-id="element-4090">
            {stats.planDistribution.map(item => <div key={item.label} data-id="element-4091">
                <div className="flex justify-between text-xs mb-1" data-id="element-4092">
                  <span className="text-gray-600" data-id="element-4093">{item.label}</span>
                  <span className="font-semibold text-gray-800" data-id="element-4094">
                    {item.count}
                  </span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-4095">
                  <div className={`h-full rounded-full ${item.color}`} style={{
                width: `${item.pct}%`
              }} data-id="element-4096" />
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* Top Learners */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4097">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-1.5" data-id="element-4098">
            <TrendingUp className="w-3.5 h-3.5" data-id="element-4099" /> Top Leerlingen
          </h3>
          {stats.topLearners.length > 0 ? <div className="space-y-2.5" data-id="element-4100">
              {stats.topLearners.map((item, i) => <div key={i} className="flex items-center gap-2.5" data-id="element-4101">
                  <div className="w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center flex-shrink-0" data-id="element-4102">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-4103">
                    <p className="text-xs font-medium text-gray-800 truncate" data-id="element-4104">
                      {item.name}
                    </p>
                  </div>
                  <span className="text-xs font-bold text-violet-600" data-id="element-4105">
                    {item.xp.toLocaleString('nl-NL')} XP
                  </span>
                </div>)}
            </div> : <p className="text-xs text-gray-400" data-id="element-4106">Geen leerlingen gevonden</p>}
        </motion.div>

        {/* Calibration Progress */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4107">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-1.5" data-id="element-4108">
            <ShieldCheck className="w-3.5 h-3.5" data-id="element-4109" /> Kalibratie
          </h3>
          <div className="space-y-3" data-id="element-4110">
            <div className="flex items-end justify-between" data-id="element-4111">
              <span className="text-2xl font-bold text-gray-900" data-id="element-4112">
                {contentStats.calibration.goldStandardPct}%
              </span>
              <span className="text-xs text-gray-500" data-id="element-4113">
                {contentStats.calibration.familiesAtGoldStandard} /{' '}
                {contentStats.calibration.totalFamilies} goud
              </span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-4114">
              <motion.div initial={{
              width: 0
            }} animate={{
              width: `${contentStats.calibration.goldStandardPct}%`
            }} transition={{
              duration: 1,
              delay: 0.3
            }} className={`h-full rounded-full ${contentStats.calibration.goldStandardPct >= 80 ? 'bg-emerald-500' : contentStats.calibration.goldStandardPct >= 40 ? 'bg-amber-500' : 'bg-red-500'}`} data-id="element-4115" />
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs" data-id="element-4116">
              <div className="flex justify-between" data-id="element-4117">
                <span className="text-gray-500" data-id="element-4118">citoDifficulty</span>
                <span className="font-semibold text-gray-700" data-id="element-4119">
                  {contentStats.calibration.citoDifficultyCoveragePct}%
                </span>
              </div>
              <div className="flex justify-between" data-id="element-4120">
                <span className="text-gray-500" data-id="element-4121">solutionSteps</span>
                <span className="font-semibold text-gray-700" data-id="element-4122">
                  {contentStats.calibration.solutionStepsCoveragePct}%
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </PageLayout>;
}